import { Product } from "@/types/entities";
import { FirebaseError } from "firebase/app";

const isString = (val?: unknown): val is string => {
    return typeof val === "string" && val.length > 0;
};

const isNumber = (val?: unknown): val is number => {
    return typeof val === "number";
};

const isObject = (val: unknown): val is object => {
    return typeof val === "object" && val !== null;
};

const isDefined = <D>(val: D | undefined): val is D => {
    return typeof val !== "undefined";
};

const hasValue = <D>(val: D | null | undefined): val is D => {
    return typeof val !== "undefined" && val !== null;
};

const isArrayWithData = <D>(val: D | undefined): val is D => {
    return Array.isArray(val) && val.length > 0;
};

const isJson = (val: string) => {
    try {
        JSON.parse(val);
        return true;
    } catch (e) {
        return false;
    }
};

const typedKeys = <O extends object>(obj: O) => {
    return Object.keys(obj) as (keyof O)[];
};

const firebaseErrorCodes = [
    "auth/insufficient-permission",
    "auth/invalid-email",
    "auth/invalid-email-verified",
    "auth/invalid-password",
    "auth/invalid-phone-number",
    "auth/invalid-login-credentials",
    "auth/invalid-credential",
    "auth/phone-number-already-exists",
    "auth/email-already-exists",
    "auth/network-request-failed",
    "auth/wrong-password",
    "auth/invalid-action-code",
    "auth/user-not-found",
    "auth/no-current-user",
    "auth/too-many-requests",
];

const ignoreFirebaseError = (error: FirebaseError) => {
    return firebaseErrorCodes.includes(error.code);
};

const ignoreError = (status: number | null) => {
    if (status === null) return false;

    if (status >= 500 && status <= 599) return true;

    return false;
};

const isFoodAndDrinks = (typeName?: Product["typeName"]) => {
    return typeName?.startsWith("10") || typeName?.startsWith("11");
};

const validateFileSize = (file: File) => {
    const sizeInKB = Math.ceil(file.size / 1024);
    if (sizeInKB > 5000) throw Error("Maximum allowed image size is 5 MBs. $sentry-ignore");
};

const validateFileType = (file: File) => {
    const type = file.type;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

    if (allowedTypes.includes(type) === false) {
        throw new Error(
            "Invalid image type. Allowed types are jpeg/jpg, png, webp. $sentry-ignore",
        );
    }
};

export {
    isString,
    isNumber,
    isObject,
    isDefined,
    hasValue,
    isArrayWithData,
    isJson,
    typedKeys,
    ignoreFirebaseError,
    ignoreError,
    isFoodAndDrinks,
    validateFileSize,
    validateFileType,
};
